/**
 * BoreholeLogWorkspace — Sprint P37y · Sprint D (Feb 2026).
 *
 * Dedicated page for continuous drillhole logs:
 *   • Select single / multiple / all boreholes
 *   • Preview each log with automatic multi-page pagination
 *   • Full-screen preview
 *   • Depth navigation (zoom / pan / jump-to-depth)
 *   • Downloads: individual PDF, combined log book PDF, PNG, CSV, Excel
 *   • Live processing states with automatic result surfacing (no manual refresh)
 *
 * Consumes existing async pipelines:
 *   POST /api/projects/{pid}/boreholes/log-book/generate
 *   GET  /api/projects/{pid}/exports/jobs/{job_id}/status
 *   GET  /api/projects/{pid}/exports/{report_id}/download
 *   GET  /api/projects/{pid}/exports/latest?kind=borehole_log_book
 */
import { useEffect, useMemo, useState } from "react";
import * as React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, ChartLineUp, DownloadSimple, Files, ArrowsOut,
  Play, ArrowClockwise, CheckCircle, CircleNotch, XCircle, Warning,
  MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowLineDown,
  FilePdf, FileCsv, FileXls, Image as ImageIcon, X, Info, Cube,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { toastReportReady } from "@/lib/reportToast";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function BoreholeLogWorkspace() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [selected, setSelected] = useState(new Set());
  const [scale, setScale] = useState(100);           // metres per page
  const [zoom,  setZoom]  = useState(1);
  const [previewing, setPreviewing] = useState(null); // hole_id | 'all'

  const collarsQ = useQuery({
    queryKey: ["log-holes", pid],
    queryFn: () => api.get(`/projects/${pid}/drillholes`).then((r) => r.data).catch(() => ({ holes: [], count: 0 })),
  });

  const latestQ = useQuery({
    queryKey: ["log-latest", pid],
    queryFn: () => api.get(`/projects/${pid}/exports/latest?kind=borehole_log_book`).then((r) => r.data).catch(() => null),
    refetchInterval: 10_000,
  });

  const jobsQ = useQuery({
    queryKey: ["log-jobs", pid],
    queryFn: () => api.get(`/exports/jobs?project_id=${pid}&kind=borehole_log_book`).then((r) => r.data).catch(() => ({ jobs: [] })),
    refetchInterval: 4000,
  });

  const enqueueMut = useMutation({
    mutationFn: () => {
      const params = new URLSearchParams();
      params.set("vertical_scale", String(scale));
      if (selected.size > 0) params.set("hole_ids", Array.from(selected).join(","));
      return api.post(`/projects/${pid}/boreholes/log-book/generate?${params.toString()}`).then((r) => r.data);
    },
    onSuccess: () => { toast.success("Log book queued"); jobsQ.refetch(); latestQ.refetch(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Enqueue failed"),
  });

  const retryMut = useMutation({
    mutationFn: (jid) => api.post(`/projects/${pid}/exports/jobs/${jid}/retry`).then((r) => r.data),
    onSuccess: () => { toast.success("Job requeued"); jobsQ.refetch(); },
  });

  // Rows
  const rows = useMemo(() => collarsQ.data?.holes || [], [collarsQ.data]);
  const holeIds = useMemo(() => rows.map((h) => h.hole_id).filter(Boolean), [rows]);
  const filtered = holeIds;

  const toggleSel = (id) => setSelected((prev) => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });
  const selectAll   = () => setSelected(new Set(filtered));
  const clearAll    = () => setSelected(new Set());
  const isAllSelected = filtered.length > 0 && selected.size === filtered.length;

  const downloadPDF = async () => {
    if (!latestQ.data?.id) { toast.error("No PDF ready yet — generate first"); return; }
    try {
      const r = await api.get(`/projects/${pid}/exports/${latestQ.data.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = latestQ.data.filename || "log_book.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Download failed"); }
  };

  const downloadCSV = async () => {
    try {
      const r = await api.get(`/projects/${pid}/data/collar/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `collars_${pid.slice(0, 8)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("CSV export not available"); }
  };

  const activeJob = (jobsQ.data?.jobs || [])
    .find((j) => j.job_status === "In Progress" || j.job_status === "Queued");
  const failedJob = (jobsQ.data?.jobs || [])
    .find((j) => j.job_status === "Failed");

  // Watch the latest log-book id — when a fresh report id appears while
  // we don't have an active job, fire the unified "Reports Panel" toast.
  const lastLatestId = React.useRef(null);
  useEffect(() => {
    const cur = latestQ.data?.id;
    if (cur && lastLatestId.current && cur !== lastLatestId.current) {
      toastReportReady({ projectId: pid, navigate: nav, kind: "Log Book" });
    }
    if (cur) lastLatestId.current = cur;
  }, [latestQ.data?.id, pid, nav]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50" data-testid="borehole-log-workspace">
      <header className="shrink-0 bg-white border-b border-slate-200 z-10">
        <div className="px-6 py-3 flex items-center gap-4">
          <button onClick={() => nav(`/app/projects/${pid}/setup`)}
                     className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-500 hover:text-brand-blue"
                     data-testid="btn-back-to-setup">
            <ArrowLeft size={12} /> Command Center
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500">Project Setup & Resource Estimation · Continuous Logs</div>
            <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900 leading-none truncate">
              Continuous Borehole Log Workspace
            </h1>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {holeIds.length} holes · {selected.size} selected · vertical scale {scale} m / page
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" onClick={() => enqueueMut.mutate()}
                       disabled={enqueueMut.isPending || (holeIds.length === 0)}
                       data-testid="btn-generate-log-book"
                       className="bg-brand-blue hover:bg-brand-blue/90 text-white text-[11.5px] gap-1.5">
              {enqueueMut.isPending ? <ArrowClockwise size={12} className="animate-spin" /> : <Play size={12} weight="fill" />}
              Generate Log Book
            </Button>
            <Button size="sm" variant="outline" onClick={downloadPDF} disabled={!latestQ.data?.id}
                       data-testid="btn-download-pdf"
                       className="text-[11.5px] gap-1"><FilePdf size={12} /> PDF</Button>
            <Button size="sm" variant="outline" onClick={downloadCSV}
                       data-testid="btn-download-csv"
                       className="text-[11.5px] gap-1"><FileCsv size={12} /> CSV</Button>
          </div>
        </div>
      </header>

      {/* Live status strip */}
      {(activeJob || failedJob) && (
        <div className={`shrink-0 px-6 py-2 border-b ${activeJob ? "bg-brand-blue/5 border-brand-blue/20" : "bg-rose-50 border-rose-200"}`}
                data-testid="job-status-strip">
          <div className="max-w-7xl mx-auto flex items-center gap-3 text-[11.5px]">
            {activeJob ? (
              <>
                <CircleNotch size={13} className="text-brand-blue animate-spin" weight="fill" />
                <span className="font-semibold text-brand-blue">Generating</span>
                <span className="text-slate-600 truncate flex-1">{activeJob.current_stage || activeJob.job_status} · {activeJob.progress || 0}%</span>
                <div className="w-32 h-1.5 bg-brand-blue/10 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-blue transition-all" style={{ width: `${activeJob.progress || 0}%` }} />
                </div>
              </>
            ) : (
              <>
                <XCircle size={13} className="text-rose-600" weight="fill" />
                <span className="font-semibold text-rose-800">Failed</span>
                <span className="text-slate-600 truncate flex-1">{failedJob.error_message || "Unknown error"}</span>
                <Button size="sm" variant="outline" onClick={() => retryMut.mutate(failedJob.id)}
                           data-testid="btn-retry-log-job"
                           className="text-[11px] text-rose-800 border-rose-300 hover:bg-rose-50">
                  <ArrowClockwise size={11} className="mr-1" /> Retry
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
                data-testid="logs-workspace-main">
        <div className="px-6 py-4 max-w-7xl mx-auto grid grid-cols-1 xl:grid-cols-[300px_1fr] gap-4">
          {/* Sidebar — controls + hole selection */}
          <aside className="space-y-3 xl:sticky xl:top-0 xl:self-start">
            <section className="rounded-lg bg-white border border-slate-200 p-3 space-y-2" data-testid="log-controls">
              <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Rendering Controls</h2>
              <label className="block">
                <div className="text-[10.5px] text-slate-500 mb-0.5">Vertical scale (m / page)</div>
                <select value={scale} onChange={(e) => setScale(Number(e.target.value))}
                          data-testid="select-vertical-scale"
                          className="w-full h-8 rounded-md border border-slate-200 bg-white px-2 text-[12px]">
                  {[50, 100, 150, 200, 250, 500].map((v) => <option key={v} value={v}>{v} m</option>)}
                </select>
              </label>
              <div className="text-[10.5px] text-slate-500">Zoom (preview only)</div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                            className="w-8 h-8 border border-slate-200 rounded hover:bg-slate-50 flex items-center justify-center">
                  <MagnifyingGlassMinus size={13} />
                </button>
                <div className="flex-1 text-center text-[11px] font-mono">{Math.round(zoom * 100)}%</div>
                <button type="button" onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
                            className="w-8 h-8 border border-slate-200 rounded hover:bg-slate-50 flex items-center justify-center">
                  <MagnifyingGlassPlus size={13} />
                </button>
              </div>
            </section>

            <section className="rounded-lg bg-white border border-slate-200 p-3 space-y-2" data-testid="hole-selector">
              <div className="flex items-center gap-2">
                <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex-1">Select Holes</h2>
                <button type="button" onClick={isAllSelected ? clearAll : selectAll}
                            data-testid="btn-select-all-holes"
                            className="text-[10.5px] text-brand-blue hover:underline">
                  {isAllSelected ? "Clear" : "All"}
                </button>
              </div>
              <div className="text-[11px] text-slate-600">
                {selected.size === 0 && holeIds.length > 0 ? `All ${holeIds.length} holes will be included by default` : `${selected.size} of ${holeIds.length} selected`}
              </div>
              <div className="max-h-64 overflow-y-auto border border-slate-100 rounded" data-testid="hole-list">
                {holeIds.length === 0 && (
                  <div className="text-[11px] text-slate-500 italic p-3 text-center">
                    No collar data available. Upload collars via the Command Center first.
                  </div>
                )}
                {holeIds.map((id) => (
                  <label key={id}
                            className={`flex items-center gap-2 px-2 py-1 border-b border-slate-100 text-[11.5px] cursor-pointer hover:bg-slate-50 ${selected.has(id) ? "bg-brand-blue/5" : ""}`}
                            data-testid={`hole-row-${id}`}>
                    <input type="checkbox"
                             checked={selected.has(id)}
                             onChange={() => toggleSel(id)}
                             data-testid={`hole-check-${id}`} />
                    <span className="font-mono text-slate-800 truncate">{id}</span>
                  </label>
                ))}
              </div>
              {holeIds.length === 0 && (
                <Link to={`/app/projects/${pid}/wizard/data_upload`}
                         className="block text-center text-[11px] text-brand-blue hover:underline">
                  Upload collars →
                </Link>
              )}
            </section>

            <section className="rounded-lg bg-white border border-slate-200 p-3 space-y-2" data-testid="latest-log-panel">
              <h2 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Latest Log Book</h2>
              {latestQ.data ? (
                <>
                  <div className="text-[12px] font-semibold text-slate-800 truncate">{latestQ.data.filename}</div>
                  <div className="text-[10.5px] text-slate-500 font-mono">
                    {latestQ.data.hole_count || "?"} holes · {Math.round((latestQ.data.file_size_bytes || 0) / 1024)} KB
                  </div>
                  <div className="text-[10px] text-slate-400">
                    {latestQ.data.created_at ? new Date(latestQ.data.created_at).toLocaleString() : ""}
                  </div>
                  <div className="flex items-center gap-1 pt-2">
                    <Button size="sm" onClick={downloadPDF} data-testid="btn-download-latest"
                               className="text-[11px] gap-1 flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">
                      <DownloadSimple size={11} weight="fill" /> Download PDF
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setPreviewing("all")}
                               data-testid="btn-preview-all"
                               className="text-[11px] gap-1"><ArrowsOut size={11} /></Button>
                  </div>
                </>
              ) : (
                <div className="text-[11px] text-slate-500 italic">No log book generated yet.</div>
              )}
            </section>
          </aside>

          {/* Main — preview grid */}
          <section className="space-y-4 min-w-0">
            {holeIds.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
                <ChartLineUp size={22} className="text-slate-400 mx-auto mb-2" weight="duotone" />
                <div className="text-[13px] text-slate-700 font-semibold">No boreholes yet</div>
                <div className="text-[11.5px] text-slate-500 mt-1 max-w-md mx-auto">
                  Upload a collar CSV, then optionally survey / assay / lithology datasets, and the continuous logs will render here automatically.
                </div>
                <Link to={`/app/projects/${pid}/wizard/data_upload`}
                         className="inline-flex items-center gap-1 mt-3 rounded-md bg-brand-blue hover:bg-brand-blue/90 text-white px-3 h-9 text-[11.5px]"
                         data-testid="btn-goto-upload">
                  <Files size={11} weight="fill" /> Open Data Upload
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="hole-grid">
                {(selected.size ? Array.from(selected) : holeIds).slice(0, 60).map((id) => (
                  <HoleCard key={id} pid={pid} holeId={id}
                                onPreview={() => setPreviewing(id)}
                                zoom={zoom} scale={scale} />
                ))}
                {holeIds.length > 60 && !selected.size && (
                  <div className="col-span-full text-[11px] text-slate-500 italic text-center py-2">
                    Showing first 60 holes — use the sidebar selector to focus on specific holes.
                  </div>
                )}
              </div>
            )}
            <div className="text-[11px] text-slate-500 flex items-start gap-2 pt-2 pb-4">
              <Info size={11} className="text-slate-400 mt-0.5" />
              <div>
                Long boreholes are automatically paginated across multiple pages at the selected vertical scale.
                Every generated PDF includes project logo, borehole header, legend, scale, coordinate grid and page numbers.
              </div>
            </div>
          </section>
        </div>
      </main>

      {previewing && (
        <FullscreenPreview pid={pid} holeId={previewing} onClose={() => setPreviewing(null)} latest={latestQ.data} />
      )}
    </div>
  );
}

// ── Custom hook: IntersectionObserver-based lazy visibility ─────────
function useInView(rootMargin = "200px") {
  const [ref, setRef] = useState(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref || inView) return;
    if (typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) { setInView(true); obs.disconnect(); break; }
      }
    }, { rootMargin });
    obs.observe(ref);
    return () => obs.disconnect();
  }, [ref, inView, rootMargin]);
  return [setRef, inView];
}

// ── Sub-components ────────────────────────────────────────────────

function HoleCard({ pid, holeId, onPreview, zoom, scale }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setRef, inView] = useInView("300px");
  useEffect(() => {
    if (!inView) return;
    let objUrl = null; let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(`/projects/${pid}/drillholes/${encodeURIComponent(holeId)}/log.png?vertical_scale=${scale}&page=1`, {
          responseType: "blob",
        });
        if (cancelled) return;
        objUrl = URL.createObjectURL(r.data);
        setUrl(objUrl);
      } catch { /* silent — hole missing data */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [pid, holeId, scale, inView]);

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/projects/${pid}/drillholes/${encodeURIComponent(holeId)}/log.pdf?vertical_scale=${scale}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = `log_${holeId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 5000);
    } catch { toast.error("PDF export failed"); }
  };

  const downloadReport = async () => {
    const t = toast.loading(`Compiling ${holeId} report…`);
    try {
      const r = await api.get(`/projects/${pid}/drillholes/${encodeURIComponent(holeId)}/report.pdf?vertical_scale=${scale}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = `${holeId}_report.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 5000);
      toast.success(`Report downloaded`, { id: t });
    } catch { toast.error("Report compilation failed", { id: t }); }
  };

  return (
    <div ref={setRef}
             className="rounded-lg border border-slate-200 bg-white overflow-hidden hover:border-brand-blue/40 hover:shadow-md transition"
             data-testid={`hole-card-${holeId}`}>
      <button type="button" onClick={onPreview} className="block w-full text-left">
        <div className="bg-slate-50 h-72 flex items-center justify-center overflow-hidden relative">
          {!inView ? (
            <div className="text-slate-300 text-[10.5px]">Scroll to load preview</div>
          ) : loading ? (
            <div className="text-slate-400 text-[11px] flex items-center gap-1"><CircleNotch size={12} className="animate-spin" /> Rendering…</div>
          ) : url ? (
            <img src={url} alt={holeId} className="w-full h-full object-contain"
                    style={{ transform: `scale(${zoom})`, transformOrigin: "top center", transition: "transform 0.2s" }}
                    data-testid={`hole-preview-${holeId}`} />
          ) : (
            <div className="text-slate-400 text-[11px] p-3 text-center">
              <ChartLineUp size={14} weight="duotone" className="mx-auto mb-1" />
              No log yet — needs collar + assay/geology
            </div>
          )}
        </div>
      </button>
      <div className="p-2 flex items-center gap-1.5 border-t border-slate-100">
        <div className="font-mono text-[12px] text-slate-800 truncate flex-1">{holeId}</div>
        <button type="button" onClick={onPreview}
                    data-testid={`hole-fullscreen-${holeId}`}
                    className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10.5px] text-slate-700 hover:bg-slate-100">
          <ArrowsOut size={10} />
        </button>
        <Link to={`/app/projects/${pid}/cockpit?hole=${encodeURIComponent(holeId)}&highlight=1`}
                   data-testid={`hole-view-3d-${holeId}`}
                   className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10.5px] text-brand-blue hover:bg-brand-blue/5"
                   title="Open in 3D cockpit with this hole highlighted">
          <Cube size={10} weight="fill" /> 3D
        </Link>
        <button type="button" onClick={downloadPdf}
                    data-testid={`hole-pdf-${holeId}`}
                    className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10.5px] text-slate-600 hover:bg-slate-100"
                    title="Download log-only PDF">
          <FilePdf size={10} /> Log
        </button>
        <button type="button" onClick={downloadReport}
                    data-testid={`hole-report-${holeId}`}
                    className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10.5px] text-brand-blue hover:bg-brand-blue/5 font-semibold"
                    title="Download full compiled drillhole report (log + charts + stats + legend)">
          <FilePdf size={10} weight="fill" /> Report
        </button>
      </div>
    </div>
  );
}

function FullscreenPreview({ pid, holeId, onClose, latest }) {
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [url, setUrl] = useState(null);
  const [scale, setScale] = useState(100);
  const [tab, setTab] = useState("log");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (holeId === "all" || tab !== "summary") return;
    let cancelled = false;
    api.get(`/projects/${pid}/drillholes/${encodeURIComponent(holeId)}/summary?cutoff=0.5`)
       .then((r) => { if (!cancelled) setSummary(r.data.summary); })
       .catch(() => { if (!cancelled) setSummary(null); });
    return () => { cancelled = true; };
  }, [pid, holeId, tab]);

  useEffect(() => {
    let objUrl = null; let cancelled = false;
    (async () => {
      // 'all' mode falls back to the log-book PDF preview
      if (holeId === "all" && latest?.id) {
        try {
          const r = await api.get(`/projects/${pid}/exports/${latest.id}/download`, { responseType: "blob" });
          if (cancelled) return;
          objUrl = URL.createObjectURL(r.data);
          setUrl(objUrl);
        } catch { /* silent */ }
        return;
      }
      try {
        const r = await api.get(`/projects/${pid}/drillholes/${encodeURIComponent(holeId)}/log.png?vertical_scale=${scale}&page=${page}`, {
          responseType: "blob",
        });
        if (cancelled) return;
        const total = parseInt(r.headers?.["x-total-pages"] || "1", 10);
        setTotalPages(Number.isFinite(total) ? total : 1);
        objUrl = URL.createObjectURL(r.data);
        setUrl(objUrl);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [pid, holeId, page, scale, latest?.id]);

  const downloadPdf = async () => {
    try {
      if (holeId === "all" && latest?.id) {
        const r = await api.get(`/projects/${pid}/exports/${latest.id}/download`, { responseType: "blob" });
        const u = URL.createObjectURL(r.data);
        const a = document.createElement("a"); a.href = u; a.download = latest.filename || "log_book.pdf";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(u), 5000);
        return;
      }
      const r = await api.get(`/projects/${pid}/drillholes/${encodeURIComponent(holeId)}/log.pdf?vertical_scale=${scale}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = `log_${holeId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 5000);
    } catch { toast.error("Download failed"); }
  };

  const downloadReport = async () => {
    if (holeId === "all") return;
    const t = toast.loading(`Compiling ${holeId} full report…`);
    try {
      const r = await api.get(`/projects/${pid}/drillholes/${encodeURIComponent(holeId)}/report.pdf?vertical_scale=${scale}`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = `${holeId}_report.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 5000);
      toast.success("Report downloaded", { id: t });
    } catch { toast.error("Report compilation failed", { id: t }); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/85 flex items-center justify-center p-4"
            data-testid="log-preview-modal">
      <div className="max-w-7xl w-full h-[95vh] bg-white rounded-lg overflow-hidden flex flex-col">
        <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-3">
          <ChartLineUp size={16} className="text-brand-blue" weight="duotone" />
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-[14px] text-slate-900 truncate">
              {holeId === "all" ? "Continuous Log Book — All Holes" : `Borehole ${holeId}`}
            </div>
            <div className="text-[10.5px] text-slate-500">
              {holeId !== "all" && tab === "log" && `Page ${page} of ${totalPages} · vertical scale ${scale} m`}
              {tab === "summary" && "Technical Summary"}
              {tab === "charts" && "Charts"}
            </div>
          </div>
          {holeId !== "all" && (
            <nav className="flex items-center gap-1 mr-2" data-testid="preview-tabs">
              {[
                { id: "log", label: "Graphical Log" },
                { id: "summary", label: "Summary" },
                { id: "charts", label: "Charts" },
              ].map((t) => (
                <button key={t.id} type="button" onClick={() => setTab(t.id)}
                            data-testid={`preview-tab-${t.id}`}
                            className={`text-[11px] px-2 py-1 rounded ${tab === t.id ? "bg-brand-blue text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                  {t.label}
                </button>
              ))}
            </nav>
          )}
          {tab === "log" && holeId !== "all" && (
            <>
              <select value={scale} onChange={(e) => { setScale(Number(e.target.value)); setPage(1); }}
                        data-testid="preview-scale"
                        className="h-8 rounded-md border border-slate-200 bg-white px-2 text-[11px]">
                {[50, 100, 200, 250, 500].map((v) => <option key={v} value={v}>{v} m/page</option>)}
              </select>
              <div className="flex items-center gap-0.5">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                            data-testid="preview-prev"
                            className="w-8 h-8 border border-slate-200 rounded hover:bg-slate-50 flex items-center justify-center disabled:opacity-40">←</button>
                <span className="text-[11px] font-mono min-w-[3rem] text-center">{page}/{totalPages}</span>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            data-testid="preview-next"
                            className="w-8 h-8 border border-slate-200 rounded hover:bg-slate-50 flex items-center justify-center disabled:opacity-40">→</button>
              </div>
            </>
          )}
          <button onClick={downloadPdf} data-testid="btn-preview-download"
                      className="inline-flex items-center gap-1 rounded-md border border-brand-blue/30 bg-white text-brand-blue hover:bg-brand-blue/5 px-2.5 h-8 text-[11.5px]">
            <FilePdf size={11} /> Log PDF
          </button>
          {holeId !== "all" && (
            <button onClick={downloadReport} data-testid="btn-preview-download-report"
                        className="inline-flex items-center gap-1 rounded-md bg-brand-blue hover:bg-brand-blue/90 text-white px-2.5 h-8 text-[11.5px]"
                        title="Compiled report: log + charts + stats + legend">
              <FilePdf size={11} weight="fill" /> Full Report
            </button>
          )}
          <button onClick={onClose} data-testid="btn-close-preview"
                      className="p-1 hover:bg-slate-100 rounded"><X size={14} /></button>
        </header>
        <div className="flex-1 bg-slate-100 overflow-auto flex items-start justify-center p-2">
          {holeId === "all" && url && (
            <iframe src={url} title={`Log-${holeId}`} className="w-full h-full border-0" data-testid="log-preview-iframe" />
          )}
          {tab === "log" && holeId !== "all" && (
            url ? <img src={url} alt={holeId} className="max-w-full" data-testid="preview-image" />
                : <div className="text-slate-500 text-[12px]"><CircleNotch size={14} className="animate-spin inline mr-2" /> Rendering log…</div>
          )}
          {tab === "summary" && <SummaryPane pid={pid} holeId={holeId} summary={summary} />}
          {tab === "charts"  && <ChartsPane pid={pid} holeId={holeId} />}
        </div>
      </div>
    </div>
  );
}


function SummaryPane({ pid, holeId, summary }) {
  if (!summary) {
    return <div className="text-slate-500 text-[12px] p-8"><CircleNotch size={14} className="animate-spin inline mr-2" /> Loading summary…</div>;
  }
  if (summary.n === 0) {
    return <div className="text-slate-500 text-[12px] p-8">No assay data for this hole.</div>;
  }
  const kpis = [
    ["Samples", summary.n], ["Min grade", summary.min], ["Max grade", summary.max],
    ["Mean grade", summary.mean], ["Median", summary.median], ["StDev", summary.stdev],
    ["P10", summary.p10], ["P25", summary.p25], ["P50", summary.p50],
    ["P75", summary.p75], ["P90", summary.p90], ["P95", summary.p95],
    ["P98", summary.p98], ["Skewness", summary.skewness], ["Kurtosis", summary.kurtosis],
    ["CV", summary.cv ?? "—"], ["Ore metres", summary.ore_metres], ["Intercepts", summary.intercept_count],
  ];
  return (
    <div className="w-full max-w-5xl mx-auto p-4 space-y-4 bg-white rounded-lg" data-testid="summary-pane">
      <h3 className="font-heading font-bold text-slate-900 text-[14px]">Descriptive Statistics · Grade (g/t)</h3>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {kpis.map(([k, v]) => (
          <div key={k} className="rounded-md border border-slate-200 bg-slate-50/40 p-2">
            <div className="text-[9.5px] uppercase tracking-widest text-slate-500">{k}</div>
            <div className="text-[13px] font-mono font-bold text-slate-800">{String(v ?? "—")}</div>
          </div>
        ))}
      </div>
      <h3 className="font-heading font-bold text-slate-900 text-[14px] pt-4">Significant Intercepts (cut-off {summary.cutoff} g/t)</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="intercepts-table">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-widest text-[9.5px]">
            <tr><th className="text-left px-2 py-1.5">From</th>
                <th className="text-left px-2 py-1.5">To</th>
                <th className="text-left px-2 py-1.5">Length</th>
                <th className="text-left px-2 py-1.5">Grade</th>
                <th className="text-left px-2 py-1.5">Samples</th>
                <th className="text-left px-2 py-1.5">Waste</th>
                <th className="text-left px-2 py-1.5">Label</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(summary.intercepts || []).map((i, k) => (
              <tr key={k}>
                <td className="px-2 py-1 font-mono">{i.from}</td>
                <td className="px-2 py-1 font-mono">{i.to}</td>
                <td className="px-2 py-1 font-mono">{i.length}</td>
                <td className="px-2 py-1 font-mono font-bold text-amber-700">{i.grade}</td>
                <td className="px-2 py-1 font-mono">{i.sample_count}</td>
                <td className="px-2 py-1 font-mono text-slate-500">{i.internal_waste}</td>
                <td className="px-2 py-1 text-amber-700 font-semibold">{i.label}</td>
              </tr>
            ))}
            {(summary.intercepts || []).length === 0 && (
              <tr><td colSpan={7} className="px-2 py-6 text-center text-slate-400 italic">No intercepts above cut-off.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChartTile({ pid, holeId, chartId, title }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [setRef, inView] = useInView("200px");
  useEffect(() => {
    if (!inView) return;
    let objUrl = null; let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/projects/${pid}/drillholes/${encodeURIComponent(holeId)}/chart/${chartId}.png`, { responseType: "blob" });
        if (cancelled) return;
        objUrl = URL.createObjectURL(r.data);
        setUrl(objUrl);
      } catch { if (!cancelled) setFailed(true); }
    })();
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [pid, holeId, chartId, inView]);
  const downloadPng = async () => {
    try {
      const r = await api.get(`/projects/${pid}/drillholes/${encodeURIComponent(holeId)}/chart/${chartId}.png`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = u; a.download = `${holeId}_${chartId}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 5000);
    } catch { toast.error("Chart not available for this hole"); }
  };
  return (
    <div ref={setRef}
             className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`chart-${chartId}`}>
      <header className="px-3 py-1.5 flex items-center gap-2 border-b border-slate-100">
        <div className="text-[12px] font-semibold text-slate-800 flex-1 truncate">{title}</div>
        <button type="button" onClick={downloadPng}
                    className="inline-flex items-center gap-0.5 text-[10.5px] text-brand-blue hover:underline disabled:opacity-40"
                    disabled={failed}
                    data-testid={`chart-download-${chartId}`}>
          <DownloadSimple size={10} /> PNG
        </button>
      </header>
      <div className="p-2 min-h-[220px] flex items-center justify-center bg-slate-50">
        {url ? (
          <img src={url} alt={title} className="max-w-full max-h-[420px] object-contain" />
        ) : failed ? (
          <div className="text-slate-400 text-[11px] text-center px-3">
            <Warning size={16} className="mx-auto mb-1 text-slate-300" weight="duotone" />
            No data available for this chart
          </div>
        ) : (
          <CircleNotch size={14} className="animate-spin text-slate-400" />
        )}
      </div>
    </div>
  );
}

function ChartsPane({ pid, holeId }) {
  const charts = [
    { id: "histogram",           title: "Grade Histogram" },
    { id: "cumulative",          title: "Cumulative Frequency" },
    { id: "grade_by_depth",     title: "Grade by Depth" },
    { id: "lithology_pie",       title: "Lithology Distribution" },
    { id: "box_plot",            title: "Grade Box Plot" },
    { id: "sample_length_hist", title: "Sample-Length Histogram" },
    { id: "recovery_rqd",        title: "Recovery & RQD by Depth" },
  ];
  return (
    <div className="w-full max-w-6xl mx-auto p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="charts-pane">
      {charts.map((c) => (
        <ChartTile key={c.id} pid={pid} holeId={holeId} chartId={c.id} title={c.title} />
      ))}
    </div>
  );
}

